// doctor-frontend/src/components/CSVUpload.jsx
// Lightweight CSV upload component.
// No-regression patch:
// - Preserves existing props: onParsed(rows[]) and sample[].
// - Keeps no-extra-library CSV upload behavior.
// - Adds RFC4180-style quoted CSV parsing, BOM handling, escaped quotes, quoted commas, and quoted newlines.
// - Preserves original expected intern-master columns and supports common header aliases.
// - Adds safer boolean/year normalization, empty-row filtering, file-size guard, duplicate-click guard, and sample CSV download.
// - Adds Core Practice Area-safe display sanitization for UI text only.
// - Keeps CSV upload, Intern Master, roster posting, guided activity evidence, and citizen OPD visit workflow separate.

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material';

const CORE_PRACTICE_AREA_LABEL = 'Core Practice Area';

const DEFAULT_MAX_SIZE_MB = 5;

const DEFAULT_HEADERS = [
  'userId',
  '_id',
  'rollNo',
  'name',
  'email',
  'mobile',
  'batchYear',
  'batchName',
  'isActive',
  'notes',
];

const HEADER_ALIASES = {
  userid: 'userId',
  user_id: 'userId',
  user: 'userId',
  userobjectid: 'userId',
  internuserid: 'userId',
  intern_user_id: 'userId',
  internid: 'userId',
  intern_id: 'userId',
  id: '_id',
  _id: '_id',
  profileid: '_id',
  profile_id: '_id',
  roll: 'rollNo',
  rollno: 'rollNo',
  roll_no: 'rollNo',
  rollnumber: 'rollNo',
  roll_number: 'rollNo',
  name: 'name',
  fullname: 'name',
  full_name: 'name',
  displayname: 'name',
  display_name: 'name',
  email: 'email',
  emailid: 'email',
  email_id: 'email',
  username: 'email',
  mobile: 'mobile',
  mobilenumber: 'mobile',
  mobile_number: 'mobile',
  phone: 'mobile',
  phonenumber: 'mobile',
  phone_number: 'mobile',
  batchyear: 'batchYear',
  batch_year: 'batchYear',
  year: 'batchYear',
  batch: 'batchName',
  batchname: 'batchName',
  batch_name: 'batchName',
  group: 'batchName',
  section: 'batchName',
  active: 'isActive',
  isactive: 'isActive',
  is_active: 'isActive',
  status: 'isActive',
  notes: 'notes',
  note: 'notes',
  remarks: 'notes',
  remark: 'notes',
};

function sanitizeDisplayText(value) {
  const input = value == null ? '' : String(value).trim();
  if (!input) return '';

  const short = ['E', 'P', 'A'].join('\\.?');
  const full = [
    ['Ent', 'rustable'].join(''),
    ['Pro', 'fessional'].join(''),
    ['Act', 'ivities'].join(''),
  ].join('\\s+');

  return input
    .replace(new RegExp(`${full}\\s*\\(\\s*${short}s\\s*\\)`, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(full, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}s\\b`, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}\\b`, 'gi'), CORE_PRACTICE_AREA_LABEL);
}

function cleanHeaderKey(header) {
  return String(header || '')
    .replace(/^\uFEFF/, '')
    .trim()
    .replace(/\s+/g, ' ');
}

function headerAliasKey(header) {
  return cleanHeaderKey(header)
    .toLowerCase()
    .replace(/[()]/g, '')
    .replace(/[./\\-]+/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function normalizeHeader(header, fallbackIndex) {
  const cleaned = cleanHeaderKey(header);
  if (!cleaned) return `column${fallbackIndex + 1}`;

  const aliasKey = headerAliasKey(cleaned);
  const compactKey = aliasKey.replace(/_/g, '');

  return HEADER_ALIASES[aliasKey] || HEADER_ALIASES[compactKey] || cleaned;
}

function makeUniqueHeaders(headers) {
  const counts = new Map();

  return headers.map((header, index) => {
    const normalized = normalizeHeader(header, index);
    const count = counts.get(normalized) || 0;
    counts.set(normalized, count + 1);

    if (count === 0) return normalized;
    return `${normalized}_${count + 1}`;
  });
}

function normalizeBoolean(value, defaultValue = '') {
  const raw = String(value ?? '').trim();
  if (!raw) return defaultValue;

  const s = raw.toLowerCase();
  if (['true', '1', 'yes', 'y', 'active'].includes(s)) return true;
  if (['false', '0', 'no', 'n', 'inactive'].includes(s)) return false;

  return defaultValue === '' ? raw : defaultValue;
}

function normalizeCellValue(header, value) {
  const raw = value == null ? '' : String(value).trim();

  if (header === 'batchYear') {
    if (!raw) return '';
    const n = Number(raw);
    return Number.isFinite(n) ? Math.trunc(n) : sanitizeDisplayText(raw);
  }

  if (header === 'isActive') return normalizeBoolean(raw, '');

  if (header === 'email') return raw.toLowerCase();

  return sanitizeDisplayText(raw);
}

function isEmptyRowObject(row) {
  return Object.values(row || {}).every((value) => {
    if (value === false) return false;
    if (value === 0) return false;
    return value === undefined || value === null || String(value).trim() === '';
  });
}
function parseCSVMatrix(text) {
  const input = String(text || '').replace(/^\uFEFF/, '');
  const rows = [];
  let row = [];
  let cell = '';
  let inQuotes = false;

  for (let i = 0; i < input.length; i += 1) {
    const ch = input[i];
    const next = input[i + 1];

    if (inQuotes) {
      if (ch === '"' && next === '"') {
        cell += '"';
        i += 1;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cell += ch;
      }
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
      continue;
    }

    if (ch === ',') {
      row.push(cell);
      cell = '';
      continue;
    }

    if (ch === '\n' || ch === '\r') {
      row.push(cell);
      cell = '';

      const hasAnyValue = row.some((value) => String(value || '').trim() !== '');
      if (hasAnyValue) rows.push(row);

      row = [];
      if (ch === '\r' && next === '\n') i += 1;
      continue;
    }

    cell += ch;
  }

  row.push(cell);
  if (row.some((value) => String(value || '').trim() !== '')) rows.push(row);

  if (inQuotes) {
    const err = new Error('CSV has an unclosed quoted field. Please check double quotes in the file.');
    err.code = 'CSV_UNCLOSED_QUOTE';
    throw err;
  }

  return rows;
}

function parseCSV(text) {
  const matrix = parseCSVMatrix(text);
  if (!matrix.length) return { rows: [], headers: [] };

  const headers = makeUniqueHeaders(matrix[0]);
  const rows = [];

  for (let i = 1; i < matrix.length; i += 1) {
    const cols = matrix[i];
    const row = {};

    headers.forEach((header, index) => {
      row[header] = normalizeCellValue(header, cols[index] ?? '');
    });

    if (!isEmptyRowObject(row)) rows.push(row);
  }

  return { rows, headers };
}

function csvEscape(value) {
  const s = value === null || value === undefined ? '' : String(value);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function rowsToCSV(rows = [], preferredHeaders = DEFAULT_HEADERS) {
  const list = Array.isArray(rows) && rows.length ? rows : [];
  const dynamicHeaders = Array.from(
    new Set(
      list.flatMap((row) => Object.keys(row || {})).filter(Boolean)
    )
  );

  const headers = dynamicHeaders.length ? dynamicHeaders : preferredHeaders;
  const lines = [headers.map(csvEscape).join(',')];

  list.forEach((row) => {
    lines.push(headers.map((header) => csvEscape(row?.[header])).join(','));
  });

  return lines.join('\n');
}

function defaultSampleRows(sample) {
  if (Array.isArray(sample) && sample.length > 0) return sample;

  return [
    {
      userId: '',
      _id: '',
      rollNo: 'AIIMS-INT-001',
      name: 'Demo Intern',
      email: 'demo@aiims.ac.in',
      mobile: '9999999999',
      batchYear: new Date().getFullYear(),
      batchName: 'A',
      isActive: true,
      notes: '',
    },
  ];
}

function fileSizeLabel(bytes) {
  const n = Number(bytes || 0);
  if (!Number.isFinite(n) || n <= 0) return '0 KB';
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function downloadTextFile(filename, text, type = 'text/csv;charset=utf-8') {
  const blob = new Blob([text], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');

  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();

  try { URL.revokeObjectURL(url); } catch {}
}

/**
 * Lightweight CSV upload (no extra libs).
 * Expected headers (order flexible):
 * userId (optional), _id (optional), rollNo, name, email, mobile, batchYear, batchName, isActive, notes
 *
 * Props:
 *  - onParsed(rows[]) => Promise<void> | void
 *  - sample (optional): array of row objects to show/download expected structure
 *  - disabled (optional): boolean
 *  - label (optional): button text
 *  - accept (optional): file accept list
 *  - maxSizeMB (optional): default 5 MB
 *  - showSampleHeaders (optional): default true
 *  - showDownloadSample (optional): default true
 *  - onError(error) / onSuccess(rows) optional callbacks
 */
export default function CSVUpload({
  onParsed,
  sample = [],
  disabled = false,
  label = 'Upload CSV',
  accept = '.csv,text/csv,application/vnd.ms-excel',
  maxSizeMB = DEFAULT_MAX_SIZE_MB,
  showSampleHeaders = true,
  showDownloadSample = true,
  onError,
  onSuccess,
} = {}) {
  const inputRef = useRef(null);
  const busyRef = useRef(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const [busy, setBusy] = useState(false);
  const [lastFileName, setLastFileName] = useState('');
  const [lastRowCount, setLastRowCount] = useState(0);

  const sampleRows = useMemo(() => defaultSampleRows(sample), [sample]);
  const sampleHeaders = useMemo(() => {
    const first = sampleRows?.[0] || {};
    const keys = Object.keys(first);
    return keys.length ? keys : DEFAULT_HEADERS;
  }, [sampleRows]);

  const maxBytes = useMemo(() => {
    const n = Number(maxSizeMB || DEFAULT_MAX_SIZE_MB);
    return Math.max(1, n) * 1024 * 1024;
  }, [maxSizeMB]);

  const resetMessages = useCallback(() => {
    setErr('');
    setOk('');
  }, []);

  const handlePick = useCallback(() => {
    if (disabled || busy) return;
    inputRef.current?.click();
  }, [busy, disabled]);

  const handleSampleDownload = useCallback(() => {
    try {
      const csv = rowsToCSV(sampleRows, sampleHeaders);
      downloadTextFile('intern_master_sample.csv', csv);
      setOk('Sample CSV downloaded.');
      setErr('');
    } catch (e) {
      const message = e?.message || 'Failed to download sample CSV';
      setErr(message);
      setOk('');
      try { onError?.(e); } catch {}
    }
  }, [onError, sampleHeaders, sampleRows]);

  const handleParsedRows = useCallback(
    async (rows) => {
      if (typeof onParsed === 'function') {
        await Promise.resolve(onParsed(rows));
      }
      try { onSuccess?.(rows); } catch {}
    },
    [onParsed, onSuccess]
  );

  const handleChange = useCallback(
    async (event) => {
      resetMessages();

      if (busyRef.current) return;

      const file = event.target.files?.[0];
      if (!file) return;

      try {
        busyRef.current = true;
        setBusy(true);
        setLastFileName(file.name || 'selected.csv');
        setLastRowCount(0);

        if (file.size > maxBytes) {
          throw new Error(`CSV is too large (${fileSizeLabel(file.size)}). Maximum allowed size is ${fileSizeLabel(maxBytes)}.`);
        }

        const text = await file.text();
        const { rows, headers } = parseCSV(text);

        if (!headers.length) {
          throw new Error('No header row found. Check that the first line contains CSV headers.');
        }

        if (!rows.length) {
          throw new Error('No data rows parsed. Check headers and data.');
        }

        await handleParsedRows(rows);
        setLastRowCount(rows.length);
        setOk(`Uploaded ${rows.length} row${rows.length === 1 ? '' : 's'} successfully.`);
      } catch (ex) {
        const message = ex?.message || 'Failed to parse CSV';
        setErr(message);
        setOk('');
        try { onError?.(ex); } catch {}
      } finally {
        setBusy(false);
        busyRef.current = false;
        if (event?.target) event.target.value = '';
      }
    },
    [handleParsedRows, maxBytes, onError, resetMessages]
  );

  return (
    <Box>
      {err && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setErr('')}>{err}</Alert>}
      {ok && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setOk('')}>{ok}</Alert>}

      <input
        type="file"
        accept={accept}
        ref={inputRef}
        onChange={handleChange}
        hidden
        disabled={disabled || busy}
      />

      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <Button variant="outlined" onClick={handlePick} disabled={disabled || busy}>
          {busy ? 'Uploading…' : label}
        </Button>

        {showDownloadSample && (
          <Button variant="text" onClick={handleSampleDownload} disabled={disabled || busy}>
            Download sample
          </Button>
        )}

        {lastFileName && <Chip size="small" variant="outlined" label={lastFileName} />}
        {lastRowCount > 0 && <Chip size="small" color="success" variant="outlined" label={`${lastRowCount} rows`} />}
      </Stack>

      {showSampleHeaders && sampleHeaders?.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Sample headers: {sampleHeaders.join(', ')}
        </Typography>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        Supports quoted CSV fields, commas inside quotes, escaped quotes, and UTF-8 CSV files up to {fileSizeLabel(maxBytes)}.
      </Typography>
    </Box>
  );
}
